import { useState } from "react";
import { Button } from "@/frontend/components/ui/button";
import { 
  Loader2, 
  Building2, 
  CheckCircle2, 
  ExternalLink,
  Copy,
  Check
} from "lucide-react";

// Layout wrapper for onboarding pages
export function OnboardingLayout({ 
  children, 
  fullWidth = false
}: { 
  children: React.ReactNode; 
  fullWidth?: boolean;
}) {
  if (fullWidth) {
    return (
      <div className="min-h-dvh bg-background flex flex-col w-full">
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background flex flex-col max-w-md mx-auto w-full">
      {children}
    </div>
  );
}

// Organization card for the link step
export function OrganizationCard({ 
  name, 
  defaultConnected = false 
}: { 
  name: string; 
  defaultConnected?: boolean;
}) {
  const [connected, setConnected] = useState(defaultConnected);
  const [connecting, setConnecting] = useState(false);
  
  const handleConnect = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false);
      setConnected(true);
    }, 1500);
  };
  
  return (
    <div
      className={`flex items-center gap-2.5 p-2.5 rounded-lg transition-all border ${
        connected 
          ? "bg-emerald-50 border-emerald-300" 
          : "bg-muted/50 border-transparent"
      }`}
    >
      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 bg-background shadow-sm">
        <Building2 className="w-4 h-4 text-muted-foreground" />
      </div>
      <p className="flex-1 min-w-0 text-sm font-medium truncate">{name}</p>
      <div className="flex items-center gap-1.5 shrink-0">
        {!connected && (
          <Button 
            size="sm" 
            onClick={handleConnect}
            disabled={connecting}
            className="gap-1 h-7 px-2.5 text-xs"
          >
            {connecting ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                Connect
                <ExternalLink className="w-3 h-3" />
              </>
            )}
          </Button>
        )}
        {connected && (
          <div className="flex items-center gap-1.5 text-emerald-600">
            <span className="text-xs font-medium">Connected</span>
            <CheckCircle2 className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}

// Pairing code component for sync step
export function PairingCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    const textToCopy = code.replace(" ", "");
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  
  return (
    <div className="flex items-center justify-center gap-3">
      <div className="text-3xl font-mono font-bold tracking-[0.15em] text-foreground">
        {code}
      </div>
      <button 
        onClick={handleCopy} 
        className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        title={copied ? "Copied!" : "Copy code"}
      >
        {copied ? (
          <Check className="w-5 h-5 text-emerald-500" />
        ) : (
          <Copy className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}

// Loading screen component
export function LoadingScreen({ text }: { text?: string } = {}) {
  return (
    <OnboardingLayout>
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        {text && <p className="text-sm text-muted-foreground">{text}</p>}
      </div>
    </OnboardingLayout>
  );
}
